const waitForMedia = (
  video: HTMLVideoElement,
  event: "seeked" | "loadeddata",
  signal?: AbortSignal,
  start?: () => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timer)
      video.removeEventListener(event, ready)
      video.removeEventListener("error", failed)
      signal?.removeEventListener("abort", aborted)
    }
    const ready = () => {
      cleanup()
      resolve()
    }
    const failed = () => {
      cleanup()
      reject(new Error("视频画面读取失败，请换一个视频或时间点重试"))
    }
    const aborted = () => {
      cleanup()
      reject(new DOMException("截取已取消", "AbortError"))
    }
    const timer = window.setTimeout(failed, 15000)
    video.addEventListener(event, ready, { once: true })
    video.addEventListener("error", failed, { once: true })
    signal?.addEventListener("abort", aborted, { once: true })
    if (signal?.aborted) aborted()
    else {
      try {
        start?.()
      } catch (error) {
        cleanup()
        reject(error)
      }
    }
  })

/** Shared by the Staff cover tool and template preview. */
export const generateVideoCover = async (
  video: HTMLVideoElement,
  time: number,
  signal?: AbortSignal,
): Promise<Blob> => {
  signal?.throwIfAborted()
  if (!Number.isFinite(video.duration)) {
    throw new Error("视频仍在加载，请稍后再试")
  }
  video.pause()
  const target = Math.min(Math.max(time, 0), Math.max(video.duration - 0.1, 0))
  if (video.seeking || Math.abs(video.currentTime - target) >= 0.05) {
    await waitForMedia(video, "seeked", signal, () => {
      video.currentTime = target
    })
  }
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await waitForMedia(video, "loadeddata", signal)
  }
  signal?.throwIfAborted()
  const canvas = document.createElement("canvas")
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const context = canvas.getContext("2d")
  if (!canvas.width || !canvas.height || !context) {
    throw new Error("无法读取视频画面")
  }
  context.drawImage(video, 0, 0, canvas.width, canvas.height)
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (value) =>
        value ? resolve(value) : reject(new Error("封面生成失败，请重试")),
      "image/jpeg",
      0.92,
    )
  })
  signal?.throwIfAborted()
  return blob
}

export const getVideoCoverErrorMessage = (error: unknown): string => {
  if (error instanceof DOMException && error.name === "SecurityError") {
    return "视频不允许跨域截取，请使用允许跨域访问的视频链接。"
  }
  return error instanceof Error ? error.message : "封面生成失败，请重试"
}
